import React, { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Plus, Pencil, Trash2, Share2, ArrowUpRight, FileText, LayoutGrid } from "lucide-react";
import Topbar from "@/components/layout/Topbar";
import { api, formatApiError } from "@/lib/api";
import { toast, Toaster } from "sonner";
import { useAuth } from "@/context/AuthContext";

const TABS_KEY = "hb_open_dashboards";

function addToWorkspace(dashboardId, dashboardName) {
  try {
    const open = JSON.parse(localStorage.getItem(TABS_KEY) || "[]");
    if (!open.some((t) => t.id === dashboardId)) {
      open.push({ id: dashboardId, name: dashboardName });
      localStorage.setItem(TABS_KEY, JSON.stringify(open));
    }
    localStorage.setItem("hb_active_tab", dashboardId);
  } catch {
    /* ignore */
  }
}

export default function MyDashboards() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/my-dashboards");
      setItems(data);
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, []);

  const remove = async (d) => {
    if (!window.confirm(`Delete "${d.name}"?`)) return;
    try {
      await api.delete(`/my-dashboards/${d.dashboard_id}`);
      toast.success("Deleted");
      load();
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  const open = (d) => {
    addToWorkspace(d.dashboard_id, d.name);
    navigate("/workspace");
  };

  return (
    <>
      <Toaster position="top-right" richColors />
      <Topbar
        title="My Dashboards"
        subtitle="Build, save, and open custom dashboards in the workspace"
        right={
          <div className="flex items-center gap-2">
            <Link
              to="/workspace"
              data-testid="goto-workspace"
              className="flex h-9 items-center gap-1.5 rounded-sm border border-slate-200 px-3 text-xs font-semibold uppercase tracking-widest text-slate-600 hover:border-navy-600 hover:text-navy-600"
            >
              <LayoutGrid className="h-3.5 w-3.5" />
              Open workspace
            </Link>
            <Link
              to="/dashboards/builder"
              data-testid="new-dashboard-button"
              className="flex h-9 items-center gap-1.5 rounded-sm bg-brand-orange px-3 text-xs font-semibold uppercase tracking-widest text-white hover:bg-[#C1701C]"
            >
              <Plus className="h-3.5 w-3.5" />
              New dashboard
            </Link>
          </div>
        }
      />

      <div className="p-6 animate-fade-in" data-testid="my-dashboards">
        {loading && <div className="text-sm text-slate-500">Loading…</div>}
        {!loading && items.length === 0 && (
          <div className="rounded-md border border-dashed border-slate-200 bg-white p-12 text-center">
            <div className="font-display text-lg font-semibold text-navy-600">No dashboards yet</div>
            <div className="mt-1 text-sm text-slate-500">
              Create your first dashboard from a catalog of 21 widgets.
            </div>
            <Link
              to="/dashboards/builder"
              className="mt-4 inline-flex items-center gap-2 rounded-sm bg-brand-orange px-4 py-2 text-xs font-semibold uppercase tracking-widest text-white hover:bg-[#C1701C]"
            >
              <Plus className="h-3.5 w-3.5" />
              Create dashboard
            </Link>
          </div>
        )}
        {!loading && items.length > 0 && (
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4">
            {items.map((d) => (
              <div
                key={d.dashboard_id}
                data-testid={`dashboard-card-${d.dashboard_id}`}
                className="group flex flex-col rounded-md border border-slate-200 bg-white shadow-sm transition-shadow hover:shadow-md"
              >
                <div className="flex items-start justify-between border-b border-slate-100 p-4">
                  <div className="flex items-center gap-3 min-w-0">
                    <div className={`flex h-9 w-9 items-center justify-center rounded-sm ${d.is_report ? "bg-brand-orange" : "bg-navy-600"} text-white`}>
                      {d.is_report ? <FileText className="h-4 w-4" /> : <LayoutGrid className="h-4 w-4" />}
                    </div>
                    <div className="min-w-0">
                      <div className="truncate font-display text-base font-semibold text-navy-600">{d.name}</div>
                      <div className="text-[10px] uppercase tracking-widest text-slate-500">
                        {d.is_report ? "REPORT" : "DASHBOARD"} · {d.widgets?.length || 0} widgets
                      </div>
                    </div>
                  </div>
                </div>
                <div className="flex-1 px-4 py-3">
                  {d.description && <p className="text-xs text-slate-500 line-clamp-2">{d.description}</p>}
                  <div className="mt-3 flex flex-wrap gap-1">
                    {(d.widgets || []).slice(0, 4).map((w) => (
                      <span key={w.instance_id} className="rounded-sm bg-slate-100 px-1.5 py-0.5 text-[10px] text-slate-500">
                        {w.widget_type.split("_").slice(0, 2).join("_")}
                      </span>
                    ))}
                    {d.widgets?.length > 4 && (
                      <span className="rounded-sm bg-slate-100 px-1.5 py-0.5 text-[10px] text-slate-500">
                        +{d.widgets.length - 4}
                      </span>
                    )}
                  </div>
                  <div className="mt-3 flex items-center gap-2 text-[10px] text-slate-400">
                    {d.is_owner ? "owned" : `shared by ${d.owner_name || d.owner_email}`}
                    {d.shared_with?.length > 0 && (
                      <span className="inline-flex items-center gap-0.5">
                        <Share2 className="h-3 w-3" />
                        {d.shared_with.length}
                      </span>
                    )}
                  </div>
                </div>
                <div className="flex border-t border-slate-100">
                  <button
                    type="button"
                    onClick={() => open(d)}
                    data-testid={`open-tab-${d.dashboard_id}`}
                    className="flex flex-1 items-center justify-center gap-1.5 px-3 py-2.5 text-xs font-semibold uppercase tracking-widest text-brand-orange hover:bg-orange-50"
                  >
                    <ArrowUpRight className="h-3.5 w-3.5" />
                    Open as tab
                  </button>
                  {d.is_owner && (
                    <>
                      <Link
                        to={`/dashboards/builder/${d.dashboard_id}`}
                        data-testid={`edit-${d.dashboard_id}`}
                        className="flex items-center justify-center px-3 py-2.5 text-slate-400 hover:bg-slate-50 hover:text-navy-600"
                      >
                        <Pencil className="h-3.5 w-3.5" />
                      </Link>
                      <button
                        type="button"
                        onClick={() => remove(d)}
                        data-testid={`delete-${d.dashboard_id}`}
                        className="flex items-center justify-center px-3 py-2.5 text-slate-400 hover:bg-red-50 hover:text-red-600"
                      >
                        <Trash2 className="h-3.5 w-3.5" />
                      </button>
                    </>
                  )}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </>
  );
}
